import { createContext, type PropsWithChildren, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import { CancellationForm } from '@/components/Cancellation/CancellationForm'
import { DetailsList } from '@/components/DetailsList/DetailsList'
import { Pillow } from '@/components/Pillow/Pillow'
import { ProductCardDetails } from '@/components/ProductCard/ProductCardDetails'
import { TotalPrice } from '@/components/TotalPrice/TotalPrice'
import { type ProductOfferFragment, useCartLazyQuery } from '@/services/graphql/generated'
import { useShopSession, useShopSessionIdOrThrow } from '@/services/shopSession/ShopSessionContext'
import { convertToDate } from '@/utils/date'
import { getSummarisedPrice } from '@/utils/getOfferPrice'
import { useFormatter } from '@/utils/useFormatter'
import { Badge } from 'ui/src/components/Badge/Badge'
import { IconButton } from 'ui/src/components/Button/IconButton'
import { Card } from 'ui/src/components/Card/Card'
import { Text } from 'ui/src/components/Text/Text'
import { ToggleTip } from 'ui/src/components/Tooltip/ToggleTip'
import { Tooltip } from 'ui/src/components/Tooltip/Tooltip'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { InfoIcon } from 'ui/src/icons/InfoIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { tokens } from 'ui/src/theme/tokens.css'
import { ProductDetails } from './components/ProductDetails'
import { ProductDocuments } from './components/ProductDocuments'
import { RemoveCartItemDialog } from './components/RemoveCartItemDialog'
import { useOfferDetails } from './useOfferDetails'

type ProductCardContextValue = { offer: ProductOfferFragment }

const ProductCardContext = createContext<ProductCardContextValue | null>(null)

const useProductCard = () => {
  const context = useContext(ProductCardContext)

  if (!context) {
    throw new Error('Make sure this component is used inside ProductCard.Root')
  }

  return context
}

type RootProps = { offer: ProductOfferFragment }
const ProductCardRoot = ({ offer, children }: PropsWithChildren<RootProps>) => {
  return (
    <ProductCardContext.Provider value={{ offer }}>
      <Card.Root>{children}</Card.Root>
    </ProductCardContext.Provider>
  )
}

type ProductCardPriceDetailsOffer = {
  cost: ProductOfferFragment['cost']
  product: Pick<ProductOfferFragment['product'], 'displayNameFull' | 'name'>
  variant: Pick<ProductOfferFragment['variant'], 'displayNameTier' | 'displayNameSubtype'>
  cancellation: Pick<ProductOfferFragment['cancellation'], 'requested'>
  startDate?: ProductOfferFragment['startDate']
  addons: ProductOfferFragment['addons']
}

const ProductCardHeader = () => {
  const {
    offer: { product, exposure, insuranceAmount },
  } = useProductCard()

  return (
    <Card.Header className={xStack({ alignItems: 'center' })}>
      <Card.Media>
        <Pillow size="small" alt={product.pillowImage.alt} role="presentation" src={product.pillowImage.src} />
      </Card.Media>

      <Card.Heading>
        <Card.Title variant={{ _: 'standard.16', sm: 'standard.18' }}>{product.displayNameFull}</Card.Title>
        <Card.Subtitle size={{ _: 'sm', sm: 'md' }}>
          {product.name === 'SE_PAYMENT_PROTECTION' && insuranceAmount
            ? insuranceAmount.displayName
            : exposure.displayNameShort}
        </Card.Subtitle>
      </Card.Heading>
    </Card.Header>
  )
}

type ProductCardDetailsSectionProps = PropsWithChildren<{ defaultExpanded?: boolean; isEditable?: boolean }>

const ProductCardDetailsSection = ({ defaultExpanded, children }: ProductCardDetailsSectionProps) => {
  const { t } = useTranslation('cart')

  const { offer } = useProductCard()

  const productDetails = useOfferDetails(offer)

  const { variant } = offer

  return (
    <ProductCardDetails.Root defaultOpen={defaultExpanded}>
      <ProductCardDetails.Trigger>
        {(isOpen) => (isOpen ? t('HIDE_DETAILS_BUTTON_LABEL') : t('SHOW_DETAILS_BUTTON_LABEL'))}
      </ProductCardDetails.Trigger>

      <ProductCardDetails.Content className={yStack({ paddingBlock: 'md', gap: 'md' })}>
        <ProductDetails details={productDetails} />

        {children}

        <ProductDocuments documents={variant.documents} />
      </ProductCardDetails.Content>
    </ProductCardDetails.Root>
  )
}

type ProductCardStartDateProps = {
  onDateChange?: () => void
}

const ProductCardStartDate = ({ onDateChange }: ProductCardStartDateProps) => {
  const shopSessionId = useShopSessionIdOrThrow()
  const { offer } = useProductCard()
  const [fetchCart] = useCartLazyQuery()

  const handleDateChange = useCallback(() => {
    // Revalidate the cart
    fetchCart({
      variables: {
        shopSessionId,
      },
      fetchPolicy: 'network-only',
    })

    onDateChange?.()
  }, [fetchCart, onDateChange, shopSessionId])

  return (
    <CancellationForm
      productOfferIds={[offer.id]}
      offer={offer}
      onStartDateChange={handleDateChange}
      onAutoSwitchChange={handleDateChange}
    />
  )
}

export function ProductCardBreakdownContent({ offer }: { offer: ProductCardPriceDetailsOffer }) {
  const { t } = useTranslation()

  const formatter = useFormatter()
  const { cost, product, variant, cancellation, startDate: offerStartDate, addons } = offer

  const startDate = convertToDate(offerStartDate)
  const formattedStartDate = startDate ? formatter.fromNow(startDate) : null

  const isAutoSwitch = cancellation.requested

  const startDateValue = isAutoSwitch ? t('CART_ENTRY_AUTO_SWITCH', { ns: 'cart' }) : formattedStartDate

  const selectedAddons = addons.filter((addon) => addon.isSelected)

  const isHomeStudent = product.name === 'SE_APARTMENT_STUDENT'
  const badgeText = isHomeStudent ? variant.displayNameSubtype : variant.displayNameTier

  return (
    <DetailsList.Root gap="xxs">
      <DetailsList.Item>
        <DetailsList.Label asChild>
          <div className={xStack({ alignItems: 'center', gap: 'xxs' })}>
            <span className={sprinkles({ flex: { _: 1, xs: 'auto' } })}>{product.displayNameFull}</span>

            {badgeText && (
              <div className={sprinkles({ flex: 1 })}>
                <Badge size="small" variant="grey2">
                  {badgeText}
                </Badge>
              </div>
            )}
          </div>
        </DetailsList.Label>
        <DetailsList.Value>
          <AccessiblePrice
            price={{ currencyCode: cost.baseInsuranceGross.currencyCode, amount: cost.baseInsuranceGross.amount }}
          />
        </DetailsList.Value>
      </DetailsList.Item>

      {selectedAddons.length > 0 &&
        selectedAddons.map((addon) => (
          <DetailsList.Item key={addon.id}>
            <DetailsList.Label className={xStack({ alignItems: 'center', gap: 'xxs' })}>
              {addon.variant.displayName}
            </DetailsList.Label>
            <DetailsList.Value>
              <AccessiblePrice price={{ currencyCode: addon.grossCost.currencyCode, amount: addon.grossCost.amount }} />
            </DetailsList.Value>
          </DetailsList.Item>
        ))}

      {cost.discountsV2.map(({ itemDiscount: discount }) => (
        <DetailsList.Item key={discount.campaignCode}>
          <DetailsList.Label>{discount.displayName}</DetailsList.Label>
          <DetailsList.Value className={xStack({ gap: 'xxs' })}>
            <Text as="span" size="xs">
              {discount.displayValue}
            </Text>
            {discount.tooltip && (
              <ToggleTip
                arrow={true}
                sideOffset={2}
                collisionPadding={8}
                Trigger={
                  <IconButton
                    data-no-hover
                    Icon={<InfoIcon size="1rem" color={tokens.colors.fills.opaque.secondary} />}
                  />
                }
              >
                <Text size="xs" color="textNegative" align="center">
                  {discount.tooltip}
                </Text>
              </ToggleTip>
            )}
          </DetailsList.Value>
        </DetailsList.Item>
      ))}

      <DetailsList.Item>
        <DetailsList.Label>{t('START_DATE_LABEL', { ns: 'cart' })}</DetailsList.Label>
        <DetailsList.Value>
          <Tooltip.Root>
            <div className={xStack({ gap: 'xxs' })}>
              <Text as="span" size="xs">
                {startDateValue}
              </Text>

              {isAutoSwitch && (
                <Tooltip.Trigger>
                  <InfoIcon />
                </Tooltip.Trigger>
              )}
            </div>
            <Tooltip.Content>
              <Text size="xs" color="textNegative" align="center">
                {t('CART_ITEM_TOOLTIP_AUTO_SWITCH', { ns: 'cart' })}
              </Text>
            </Tooltip.Content>
          </Tooltip.Root>
        </DetailsList.Value>
      </DetailsList.Item>
    </DetailsList.Root>
  )
}

const ProductCardBreakdown = () => {
  const { offer } = useProductCard()
  return <ProductCardBreakdownContent offer={offer} />
}

export function ProductCardTotalPriceContent({ offer }: { offer: ProductCardPriceDetailsOffer }) {
  const { t } = useTranslation('common')
  const { cost } = offer

  const price = getSummarisedPrice(cost)

  return <TotalPrice {...price} label={t('YOUR_PRICE')} />
}

const ProductCardTotalPrice = () => {
  const { offer } = useProductCard()
  return <ProductCardTotalPriceContent offer={offer} />
}

const ProductCardRemoveButton = () => {
  const { offer } = useProductCard()
  const { shopSession } = useShopSession()

  if (!shopSession) {
    return null
  }

  return (
    <RemoveCartItemDialog.Root shopSessionId={shopSession.id} offer={offer}>
      <Card.Aside floating>
        <RemoveCartItemDialog.Trigger>
          <IconButton variant="secondary">
            <CrossIcon />
          </IconButton>
        </RemoveCartItemDialog.Trigger>
      </Card.Aside>
    </RemoveCartItemDialog.Root>
  )
}

export const ProductCard = {
  Root: ProductCardRoot,
  Header: ProductCardHeader,
  Details: ProductCardDetailsSection,
  StartDate: ProductCardStartDate,
  Breakdown: ProductCardBreakdown,
  TotalPrice: ProductCardTotalPrice,
  RemoveButton: ProductCardRemoveButton,
}
